import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { FiFileText, FiCheckCircle, FiStar, FiGlobe, FiUpload, FiX, FiCheck } from "react-icons/fi";
import { FaFacebook, FaGoogle } from "react-icons/fa";
import { publicRequest } from "../../requestMethods";
import StepCodeTag from "../../components/shared/StepCodeTag";
import { CUSTOMER_SURVEY_STEP_CODES } from "../../constants/workflowStepCodes";

const labelWithTagStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

const LabelWithTag = ({ code, children }) => (
    <span style={labelWithTagStyle}>
        {code && <StepCodeTag code={code} />}
        {children}
    </span>
);

const translations = {
    en: {
        pageTitle: "Customer Satisfaction Survey",
        pageSubtitle: "Collect customer feedback",
        dearClient: "Dear Valued Client,",
        companyName: "INHOME GROUP SDN BHD",
        introText: "strives to provide our clients with the highest possible level of services and projects. In order for us to continuously improve our services, could you spare some time and participate in our Satisfaction Survey by giving us your views on the following criteria:",
        serviceRating: "Service Rating Criteria",
        selectRating: "Please select one rating for each criteria",
        criteria: "Criteria",
        excellent: "Excellent",
        good: "Good",
        average: "Average",
        poor: "Poor",
        unacceptable: "Unacceptable",
        pricing: "1. Pricing",
        projectCompletion: "2. Project Completion",
        qualityOfWork: "3. Quality of work",
        communication: "4. Communication",
        maintenance: "5. Maintenance",
        overallRating: "Overall Rating",
        overallRatingDesc: "Please rate your overall satisfaction with our service",
        commentsLabel: "Please tell us your comments or suggestion in order to improve our service to you:",
        commentsPlaceholder: "Your comments and suggestions...",
        orderNo: "Confirmation Order No:",
        customerName: "Customer Name:",
        address: "Address:",
        date: "Date:",
        time: "Time:",
        signature: "Signature:",
        clearSignature: "Clear Signature",
        signatureHelp: "Draw your signature above using your mouse or touch",
        saveSurvey: "Save Survey",
        saved: "Saved",
        saving: "Saving...",
        generatePdf: "Generate PDF",
        loading: "Loading survey...",
        orderNotFound: "Order not found.",
        loadError: "Failed to load survey. Please check the link.",
        saveSuccess: "Survey saved successfully. Thank you!",
        saveError: "Failed to save survey. Please try again.",
        excellentText: "Excellent!",
        veryGoodText: "Very Good!",
        goodText: "Good",
        fairText: "Fair",
        poorText: "Poor",
        facebookReview: "Facebook Review",
        googleReview: "Google Review",
        uploadImage: "Upload Image",
        replaceImage: "Replace Image",
        reviewScreenshot: "Upload a screenshot of your review",
        uploading: "Uploading...",
        statusPending: "Pending",
        statusApproved: "Approved",
        statusRejected: "Rejected",
    },
    zh: {
        pageTitle: "客户满意度调查",
        pageSubtitle: "收集客户反馈",
        dearClient: "尊敬的客户，",
        companyName: "INHOME GROUP SDN BHD",
        introText: "致力于为客户提供最高水平的服务和项目。为了不断改进我们的服务，请您抽出一些时间参与我们的满意度调查，就以下标准给出您的意见：",
        serviceRating: "服务评分标准",
        selectRating: "请为每个标准选择一个评分",
        criteria: "标准",
        excellent: "优秀",
        good: "良好",
        average: "一般",
        poor: "较差",
        unacceptable: "不可接受",
        pricing: "1. 价格",
        projectCompletion: "2. 项目完成度",
        qualityOfWork: "3. 工作质量",
        communication: "4. 沟通",
        maintenance: "5. 维护",
        overallRating: "总体评分",
        overallRatingDesc: "请对我们的服务进行总体满意度评分",
        commentsLabel: "请告诉我们您的意见或建议，以便我们改进服务：",
        commentsPlaceholder: "您的意见和建议...",
        orderNo: "订单确认号：",
        customerName: "客户姓名：",
        address: "地址：",
        date: "日期：",
        time: "时间：",
        signature: "签名：",
        clearSignature: "清除签名",
        signatureHelp: "请使用鼠标或触摸在上方绘制您的签名",
        saveSurvey: "保存调查",
        saved: "已保存",
        saving: "保存中...",
        generatePdf: "生成PDF",
        loading: "加载调查中...",
        orderNotFound: "未找到订单。",
        loadError: "加载调查失败，请检查链接。",
        saveSuccess: "调查保存成功，谢谢！",
        saveError: "保存调查失败，请重试。",
        excellentText: "优秀！",
        veryGoodText: "非常好！",
        goodText: "良好",
        fairText: "一般",
        poorText: "较差",
        facebookReview: "Facebook 评价",
        googleReview: "Google 评价",
        uploadImage: "上传图片",
        replaceImage: "替换图片",
        reviewScreenshot: "上传您的评价截图",
        uploading: "上传中...",
        statusPending: "待审核",
        statusApproved: "已通过",
        statusRejected: "已拒绝",
    },
};

const getCriteriaList = (t) => [
    { key: "pricing", label: t.pricing, stepCode: CUSTOMER_SURVEY_STEP_CODES.pricing },
    { key: "projectCompletion", label: t.projectCompletion, stepCode: CUSTOMER_SURVEY_STEP_CODES.projectCompletion },
    { key: "qualityOfWork", label: t.qualityOfWork, stepCode: CUSTOMER_SURVEY_STEP_CODES.qualityOfWork },
    { key: "communication", label: t.communication, stepCode: CUSTOMER_SURVEY_STEP_CODES.communication },
    { key: "maintenance", label: t.maintenance, stepCode: CUSTOMER_SURVEY_STEP_CODES.maintenance },
];

const initialSurveyData = {
    pricing: "",
    projectCompletion: "",
    qualityOfWork: "",
    communication: "",
    maintenance: "",
    starRating: "",
    comments: "",
    orderNumber: "",
    customerName: "",
    address: "",
    installerName: "",
    date: "",
    time: "",
    icNumber: "",
    signature: "",
};

export default function PublicSurvey() {
    const { id: orderId } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [surveyData, setSurveyData] = useState(initialSurveyData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSavingSurvey, setIsSavingSurvey] = useState(false);
    const [saved, setSaved] = useState(false);
    const [hoveredStar, setHoveredStar] = useState(0);
    const signatureRef = useRef(null);
    const [language, setLanguage] = useState("en");
    const t = translations[language];
    const CRITERIA = getCriteriaList(t);
    const [facebookReviewUrl, setFacebookReviewUrl] = useState("");
    const [googleReviewUrl, setGoogleReviewUrl] = useState("");
    const [isUploadingFacebook, setIsUploadingFacebook] = useState(false);
    const [isUploadingGoogle, setIsUploadingGoogle] = useState(false);
    const facebookInputRef = useRef(null);
    const googleInputRef = useRef(null);

    const getReviewStatusBadge = (status) => {
        switch (status) {
            case "approved":
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FiCheck className="w-3 h-3 mr-1" />
                        {t.statusApproved}
                    </span>
                );
            case "rejected":
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <FiX className="w-3 h-3 mr-1" />
                        {t.statusRejected}
                    </span>
                );
            case "pending":
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t.statusPending}
                    </span>
                );
        }
    };

    useEffect(() => {
        const fetchOrder = async () => {
            if (!orderId) return;
            setLoading(true);
            setError(null);
            try {
                const res = await publicRequest.get(`/orders/findOrder/${orderId}`);
                const order = res.data;
                setOrderData(order);
                const addr = [
                    order?.lineOne,
                    order?.lineTwo,
                    order?.zip,
                    order?.city,
                    order?.country,
                ]
                    .filter(Boolean)
                    .join(", ");
                setSurveyData({
                    ...initialSurveyData,
                    orderNumber: order?.coNumber || order?.invoiceId || "",
                    customerName: order?.userId?.name || "",
                    address: addr,
                    installerName: order?.installer?.name || "",
                    pricing: order?.survey?.pricing || "",
                    projectCompletion: order?.survey?.projectCompletion || "",
                    qualityOfWork: order?.survey?.qualityOfWork || "",
                    communication: order?.survey?.communication || "",
                    maintenance: order?.survey?.maintenance || "",
                    starRating: order?.survey?.starRating?.toString() || "",
                    comments: order?.survey?.comments || "",
                    date: order?.survey?.surveyDate || "",
                    time: order?.survey?.time || "",
                    icNumber: order?.survey?.ic || order?.userId?.ic || "",
                    signature: order?.survey?.signature || "",
                });
                setFacebookReviewUrl(order?.survey?.facebookReview?.url || "");
                setGoogleReviewUrl(order?.survey?.googleReview?.url || "");
            } catch (err) {
                console.error("Error fetching order:", err);
                setError(err?.response?.status === 404 ? "Order not found." : "Failed to load survey. Please check the link.");
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId]);

    const drawSignatureOnCanvas = (canvas, signatureSrc) => {
        if (!canvas || !signatureSrc) return;
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.onerror = () => console.error("Failed to load signature image");
        img.src = signatureSrc;
    };

    useEffect(() => {
        if (!surveyData.signature) return;
        const canvas = signatureRef.current;
        if (canvas) {
            drawSignatureOnCanvas(canvas, surveyData.signature);
        } else {
            const t = setTimeout(() => {
                if (signatureRef.current) {
                    drawSignatureOnCanvas(signatureRef.current, surveyData.signature);
                }
            }, 100);
            return () => clearTimeout(t);
        }
    }, [surveyData.signature]);

    const handleSurveyChange = (field, value) => {
        setSurveyData((prev) => ({ ...prev, [field]: value }));
    };

    const clearSignature = () => {
        if (signatureRef.current) {
            const canvas = signatureRef.current;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setSurveyData((prev) => ({ ...prev, signature: "" }));
        }
    };

    const handleSignatureEnd = () => {
        if (signatureRef.current) {
            const signatureData = signatureRef.current.toDataURL();
            setSurveyData((prev) => ({ ...prev, signature: signatureData }));
        }
    };

    const handleReviewImageUpload = async (file, type) => {
        if (!file || !orderData?._id) return null;
        
        const setUploading = type === "facebook" ? setIsUploadingFacebook : setIsUploadingGoogle;
        setUploading(true);
        
        try {
            const imageResult = await publicRequest.get("/imagekit/public");
            const imageFormData = new FormData();
            const fileName = `${Date.now()}_${file.name}`;
            
            imageFormData.append("file", file);
            imageFormData.append("publicKey", imageResult.data.publicKey);
            imageFormData.append("signature", imageResult.data.signature);
            imageFormData.append("expire", imageResult.data.expire);
            imageFormData.append("token", imageResult.data.token);
            imageFormData.append("fileName", `${fileName} - ${imageResult.data.expire}`);
            imageFormData.append("folder", "reviews");
            
            const uploadResult = await axios.post(
                "https://upload.imagekit.io/api/v1/files/upload",
                imageFormData
            );
            
            const url = uploadResult.data.url;
            
            const reviewKey = type === "facebook" ? "facebookReview" : "googleReview";
            const updatedReview = {
                url: url,
                status: "pending",
            };
            
            const updatePayload = {
                survey: {
                    ...orderData.survey,
                    [reviewKey]: updatedReview,
                },
            };
            
            await publicRequest.put(`/orders/update/${orderData._id}`, updatePayload);
            
            if (type === "facebook") {
                setFacebookReviewUrl(url);
            } else {
                setGoogleReviewUrl(url);
            }
            
            setOrderData(prev => ({
                ...prev,
                survey: {
                    ...prev.survey,
                    [reviewKey]: updatedReview,
                },
            }));
            
            return url;
        } catch (error) {
            console.error(`Error uploading ${type} review image:`, error);
            alert(`Failed to upload image. Please try again.`);
            return null;
        } finally {
            setUploading(false);
        }
    };

    const saveSurvey = async () => {
        if (!orderData?._id) return;
        setIsSavingSurvey(true);
        try {
            const currentDate = surveyData.date || new Date().toISOString();
            const currentTime =
                surveyData.time ||
                new Date().toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                });
            const customerIc = orderData.userId?.ic || surveyData.icNumber || "";

            const surveyPayload = {
                pricing: surveyData.pricing,
                projectCompletion: surveyData.projectCompletion,
                qualityOfWork: surveyData.qualityOfWork,
                communication: surveyData.communication,
                maintenance: surveyData.maintenance,
                starRating: parseInt(surveyData.starRating) || undefined,
                comments: surveyData.comments,
                signature: surveyData.signature,
                surveyDate: currentDate,
                time: currentTime,
                ic: customerIc,
                facebookReview: facebookReviewUrl ? { url: facebookReviewUrl, status: "pending" } : undefined,
                googleReview: googleReviewUrl ? { url: googleReviewUrl, status: "pending" } : undefined,
            };

            const today = new Date();
            const commenceDate = today.toISOString().split("T")[0];
            let completionFromApproval = null;
            if (orderData.createdAt) {
                const blueprintDate = new Date(orderData.createdAt);
                const commence = new Date(commenceDate);
                blueprintDate.setHours(0, 0, 0, 0);
                commence.setHours(0, 0, 0, 0);
                const diffTime = commence - blueprintDate;
                completionFromApproval =
                    Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
            }

            const payload = {
                survey: surveyPayload,
                status: "installApproved",
                commenceDate,
                completionFromApproval,
            };

            await publicRequest.put(`/orders/update/${orderData._id}`, payload);
            setOrderData((prev) => ({
                ...prev,
                survey: surveyPayload,
                status: "installApproved",
            }));
            setSurveyData((prev) => ({
                ...prev,
                date: currentDate,
                time: currentTime,
                icNumber: customerIc,
            }));
            setSaved(true);
            alert(t.saveSuccess);
        } catch (err) {
            console.error("Error saving survey:", err);
            alert(t.saveError);
        } finally {
            setIsSavingSurvey(false);
        }
    };

    const formattedAddress = [
        orderData?.lineOne,
        orderData?.lineTwo,
        orderData?.zip,
        orderData?.city,
        orderData?.country,
    ]
        .filter(Boolean)
        .join(", ");

    const generateSurveyPDFOnly = () => {
        // Match Survey tab: use same field names as orderData.survey (surveyDate, time, ic)
        const savedSurvey = {
            pricing: surveyData.pricing,
            projectCompletion: surveyData.projectCompletion,
            qualityOfWork: surveyData.qualityOfWork,
            communication: surveyData.communication,
            maintenance: surveyData.maintenance,
            starRating: surveyData.starRating ? parseInt(surveyData.starRating, 10) : undefined,
            comments: surveyData.comments,
            signature: surveyData.signature,
            surveyDate: surveyData.date,
            time: surveyData.time,
            ic: surveyData.icNumber,
        };
        const printWindow = window.open("", "_blank");
        if (!printWindow) return;
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Customer Satisfaction Survey</title>
                <meta charset="UTF-8">
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                    * { box-sizing: border-box; }
                    body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 40px; line-height: 1.6; color: #1a1a1a; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); min-height: 100vh; }
                    .document-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1); overflow: hidden; }
                    .header-section { background: rgb(246, 173, 85); color: white; padding: 40px; text-align: center; position: relative; overflow: hidden; }
                    .header-section::before { content: ''; position: absolute; top: -50%; right: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%); animation: gentle-float 6s ease-in-out infinite; }
                    @keyframes gentle-float { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-10px, -10px) rotate(5deg); } }
                    .company-name { font-size: 28px; font-weight: 700; margin-bottom: 8px; letter-spacing: -0.5px; position: relative; z-index: 2; }
                    .document-title { font-size: 18px; font-weight: 500; opacity: 0.95; margin-bottom: 0; letter-spacing: 2px; text-transform: uppercase; position: relative; z-index: 2; }
                    .content-section { padding: 40px; }
                    .intro-card { background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); border-left: 4px solid #6366f1; padding: 24px; margin-bottom: 32px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); }
                    .intro-text { color: #374151; font-size: 14px; line-height: 1.7; margin: 0; }
                    .criteria-table { width: 100%; border-collapse: separate; border-spacing: 0; margin-bottom: 24px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); }
                    .criteria-table th { background: linear-gradient(135deg, #1f2937 0%, #374151 100%); color: white; padding: 16px 12px; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; border: none; }
                    .criteria-table td { padding: 14px 12px; border-bottom: 1px solid #e5e7eb; text-align: center; font-size: 13px; background: white; }
                    .criteria-table tr:nth-child(even) td { background: #f9fafb; }
                    .criteria-name { text-align: left !important; font-weight: 500; color: #374151; }
                    .rating-cell { width: 60px; position: relative; }
                    .rating-mark { color: #059669; font-size: 18px; font-weight: 700; }
                    .comments-section { background: #f8fafc; border-radius: 8px; padding: 24px; margin: 24px 0; border: 1px solid #e2e8f0; }
                    .form-field { background: #f8fafc; border-radius: 8px; padding: 16px; border: 1px solid #e2e8f0; margin: 16px 0; }
                    .field-value { color: #1f2937; font-size: 14px; font-weight: 500; padding: 8px 0; border-bottom: 2px solid #6366f1; min-height: 24px; }
                    .underline { text-decoration: underline; }
                    @media print { body { margin: 0; padding: 20px; background: white; } .document-container { box-shadow: none; border-radius: 0; } .no-print { display: none !important; } .header-section::before { display: none; } }
                    @media (max-width: 768px) { body { padding: 20px; } .content-section { padding: 24px; } .header-section { padding: 24px; } .company-name { font-size: 24px; } }
                </style>
            </head>
            <body>
                <div class="document-container">
                    <div class="header-section">
                        <div class="company-name">INHOME GROUP SDN BHD</div>
                        <div class="document-title">Customer Satisfaction Survey</div>
                    </div>
                    <div class="content-section">
                        <div class="intro-card">
                            <p class="intro-text">
                                <strong>Dear Valued Client,</strong><br><br>
                                We strive to provide our clients with the highest possible level of services and projects.
                                To continuously improve our services, we would appreciate your participation in this
                                satisfaction survey by sharing your views on the following criteria.
                            </p>
                        </div>
                        <div class="intro" style="margin-bottom: 16px;">
                            Dear our valued client,<br><br>
                            <strong>INHOME GROUP SDN BHD</strong> strives to provide our client with the highest possible level of
                            services and projects. In order for us to continuously improve our services, could you spare some
                            time and participate in our Satisfaction Survey by giving us your views on the following:
                        </div>
                        <div style="margin-bottom: 15px; font-size: 12px;">Please tick (√) one</div>
                        <table class="criteria-table">
                            <thead>
                                <tr>
                                    <th rowspan="2" style="width: 80px;">Criteria</th>
                                    <th>Excellent</th><th>Good</th><th>Average</th><th>Poor</th><th>Unacceptable</th>
                                </tr>
                                <tr>
                                    <th class="rating-cell">5</th><th class="rating-cell">4</th><th class="rating-cell">3</th><th class="rating-cell">2</th><th class="rating-cell">1</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="criteria-name">1. Pricing</td>
                                    <td class="rating-cell">${savedSurvey.pricing === "5" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.pricing === "4" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.pricing === "3" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.pricing === "2" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.pricing === "1" ? '<span class="rating-mark">√</span>' : ""}</td>
                                </tr>
                                <tr>
                                    <td class="criteria-name">2. Project Completion</td>
                                    <td class="rating-cell">${savedSurvey.projectCompletion === "5" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.projectCompletion === "4" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.projectCompletion === "3" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.projectCompletion === "2" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.projectCompletion === "1" ? '<span class="rating-mark">√</span>' : ""}</td>
                                </tr>
                                <tr>
                                    <td class="criteria-name">3. Quality of work</td>
                                    <td class="rating-cell">${savedSurvey.qualityOfWork === "5" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.qualityOfWork === "4" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.qualityOfWork === "3" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.qualityOfWork === "2" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.qualityOfWork === "1" ? '<span class="rating-mark">√</span>' : ""}</td>
                                </tr>
                                <tr>
                                    <td class="criteria-name">4. Communication</td>
                                    <td class="rating-cell">${savedSurvey.communication === "5" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.communication === "4" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.communication === "3" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.communication === "2" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.communication === "1" ? '<span class="rating-mark">√</span>' : ""}</td>
                                </tr>
                                <tr>
                                    <td class="criteria-name">5. Maintenance</td>
                                    <td class="rating-cell">${savedSurvey.maintenance === "5" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.maintenance === "4" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.maintenance === "3" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.maintenance === "2" ? '<span class="rating-mark">√</span>' : ""}</td>
                                    <td class="rating-cell">${savedSurvey.maintenance === "1" ? '<span class="rating-mark">√</span>' : ""}</td>
                                </tr>
                            </tbody>
                        </table>
                        ${savedSurvey.starRating ? `
                        <div class="form-field" style="margin: 24px 0; padding: 20px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #fbbf24; border-radius: 8px;">
                            <div style="margin-bottom: 12px; font-weight: 600; color: #92400e; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">Overall Rating</div>
                            <div style="display: flex; align-items: center; gap: 8px;">
                                ${Array.from({ length: 5 }, (_, i) => {
                                    const starNum = i + 1;
                                    const isFilled = starNum <= parseInt(savedSurvey.starRating, 10);
                                    return `<span style="font-size: 24px; color: ${isFilled ? "#f59e0b" : "#d1d5db"}; ${isFilled ? "text-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);" : ""}">★</span>`;
                                }).join("")}
                                <span style="margin-left: 12px; font-size: 18px; font-weight: 700; color: #92400e;">${savedSurvey.starRating} / 5</span>
                                ${parseInt(savedSurvey.starRating, 10) === 5 ? '<span style="margin-left: 8px; color: #059669; font-weight: 600;">Excellent!</span>' :
                                  parseInt(savedSurvey.starRating, 10) === 4 ? '<span style="margin-left: 8px; color: #059669; font-weight: 600;">Very Good!</span>' :
                                  parseInt(savedSurvey.starRating, 10) === 3 ? '<span style="margin-left: 8px; color: #d97706; font-weight: 600;">Good</span>' :
                                  parseInt(savedSurvey.starRating, 10) === 2 ? '<span style="margin-left: 8px; color: #dc2626; font-weight: 600;">Fair</span>' :
                                  parseInt(savedSurvey.starRating, 10) === 1 ? '<span style="margin-left: 8px; color: #dc2626; font-weight: 600;">Poor</span>' : ""}
                            </div>
                        </div>
                        ` : ""}
                        <div class="comments-section">
                            <div style="margin-bottom: 10px; font-weight: bold;">Please tell us your comments or suggestion in order to improve our service to you:</div>
                            <div style="border: 1px solid black; min-height: 60px; padding: 10px; background-color: #f9f9f9;">${savedSurvey.comments || ""}</div>
                        </div>
                        <div class="form-field">Confirmation Order no: <span class="underline">${orderData?.coNumber || orderData?.invoiceId || "N/A"}</span></div>
                        <div class="form-field">Customer Name: <span class="underline">${orderData?.userId?.name || "N/A"}</span></div>
                        <div class="form-field">Address: <span class="underline">${formattedAddress || "N/A"}</span></div>
                        <div class="form-field">Date: <span class="underline">${savedSurvey.surveyDate ? moment(savedSurvey.surveyDate).format("DD MMM YYYY") : "N/A"}</span></div>
                        <div class="form-field">Time: <span class="underline">${savedSurvey.time || "N/A"}</span></div>
                        <div class="form-field">IC Number: <span class="underline">${savedSurvey.ic || orderData?.userId?.ic || "N/A"}</span></div>
                        <div class="form-field">
                            <div style="margin-bottom: 10px; font-weight: bold;">Signature:</div>
                            <div style="border: 1px solid black; height: 100px; display: flex; align-items: center; justify-content: center; background-color: #f9f9f9;">
                                ${savedSurvey.signature ? `<img src="${savedSurvey.signature}" style="max-height: 90px; max-width: 300px;" alt="Signature" />` : '<span style="color: #666;">No signature provided</span>'}
                            </div>
                        </div>
                        <div class="no-print" style="margin-top: 30px; text-align: center;">
                            <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; background-color: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">Print Survey</button>
                            <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; background-color: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">Close</button>
                        </div>
                    </div>
                </div>
            </body>
            </html>`;
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    const drawHandlers = (e, isTouch = false) => {
        const canvas = e.target;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext("2d");
        const getCoords = (ev) => {
            const evt = isTouch ? ev.touches[0] : ev;
            return {
                x: (evt.clientX - rect.left) * (canvas.width / rect.width),
                y: (evt.clientY - rect.top) * (canvas.height / rect.height),
            };
        };
        let isDrawing = false;
        const start = (ev) => {
            isDrawing = true;
            const { x, y } = getCoords(ev);
            ctx.beginPath();
            ctx.moveTo(x, y);
        };
        const draw = (ev) => {
            if (!isDrawing) return;
            const { x, y } = getCoords(ev);
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.strokeStyle = "#000";
            ctx.lineTo(x, y);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x, y);
        };
        const stop = () => {
            if (isDrawing) {
                isDrawing = false;
                handleSignatureEnd();
            }
        };
        start(e);
        const move = isTouch
            ? (ev) => {
                  ev.preventDefault();
                  draw(ev);
              }
            : draw;
        const up = (ev) => {
            if (isTouch) ev.preventDefault();
            stop();
            document.removeEventListener(isTouch ? "touchmove" : "mousemove", move);
            document.removeEventListener(isTouch ? "touchend" : "mouseup", up);
        };
        document.addEventListener(isTouch ? "touchmove" : "mousemove", move, { passive: false });
        document.addEventListener(isTouch ? "touchend" : "mouseup", up);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-gray-600 text-lg">{t.loading}</div>
            </div>
        );
    }

    if (error || !orderData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center text-red-600 text-lg px-4">
                    {error || t.orderNotFound}
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.page}>
                            {t.pageTitle}
                        </LabelWithTag>
                    </h1>
                    <p className="text-gray-600 text-sm">
                        {t.pageSubtitle}
                    </p>
                </div>
                <button
                    onClick={() => setLanguage(language === "en" ? "zh" : "en")}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors self-start md:self-auto"
                >
                    <FiGlobe className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-700">
                        {language === "en" ? "中文" : "English"}
                    </span>
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-200">
                <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                            <FiStar className="w-6 h-6 text-white" />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{t.dearClient}</h3>
                        <p className="text-gray-700 leading-relaxed">
                            <span className="font-bold text-blue-600">{t.companyName}</span> {t.introText}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 mb-8">
                <div className="text-white py-4 px-6" style={{ backgroundColor: "#f6ad55" }}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold flex items-center">
                            <FiStar className="mr-2 h-5 w-5" />
                            {t.serviceRating}
                        </h3>
                        <p className="text-sm opacity-90">{t.selectRating}</p>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr className="bg-gray-100">
                                <th rowSpan={2} className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">{t.criteria}</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{t.excellent}</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{t.good}</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{t.average}</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{t.poor}</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{t.unacceptable}</th>
                            </tr>
                            <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">5</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">4</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">3</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">2</th>
                                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">1</th>
                            </tr>
                        </thead>
                        <tbody>
                            {CRITERIA.map((criteria, index) => (
                                <tr key={criteria.key} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="border border-gray-300 px-4 py-3 font-medium text-gray-700">
                                        <LabelWithTag code={criteria.stepCode}>
                                            {criteria.label}
                                        </LabelWithTag>
                                    </td>
                                    {[5, 4, 3, 2, 1].map((rating) => (
                                        <td key={rating} className="border border-gray-300 px-4 py-3 text-center">
                                            <input
                                                type="radio"
                                                name={criteria.key}
                                                value={rating}
                                                checked={surveyData[criteria.key] === rating.toString()}
                                                onChange={(e) => handleSurveyChange(criteria.key, e.target.value)}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-2xl shadow-xl p-3 md:p-8 mb-4 md:mb-8 border border-gray-200">
                <div className="mb-3 md:mb-6">
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 flex items-center">
                        <div className="w-6 h-6 md:w-10 md:h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mr-2 md:mr-3 shadow-md">
                            <FiStar className="w-3 h-3 md:w-5 md:h-5 text-white fill-white" />
                        </div>
                        <span className="text-sm md:text-xl">
                            <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.overallRating}>
                                {t.overallRating}
                            </LabelWithTag>
                        </span>
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 md:ml-14 mb-2 md:mb-0">
                        {t.overallRatingDesc}
                    </p>
                </div>
                {/* Mobile: Stars inline with text, Desktop: Centered - same as SurveyTab */}
                <div className="flex flex-col items-start md:items-center justify-start md:justify-center py-0 md:py-4">
                    <div
                        className="flex items-center gap-1 md:space-x-3 md:mb-4"
                        onMouseLeave={() => setHoveredStar(0)}
                    >
                        {[1, 2, 3, 4, 5].map((star) => {
                            const currentRating = parseInt(surveyData.starRating) || 0;
                            const hoverRating = hoveredStar || currentRating;
                            const isFilled = star <= hoverRating;
                            const isActive = star <= currentRating;
                            return (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => {
                                        const newRating = currentRating === star ? 0 : star;
                                        handleSurveyChange("starRating", newRating.toString());
                                        setHoveredStar(0);
                                    }}
                                    onMouseEnter={() => setHoveredStar(star)}
                                    className="group relative transition-all duration-300 cursor-pointer hover:scale-125 active:scale-110"
                                >
                                    <FiStar
                                        className={`w-4 h-4 md:w-12 md:h-12 transition-all duration-300 ${
                                            isFilled
                                                ? isActive
                                                    ? "text-yellow-500 fill-yellow-500 drop-shadow-lg"
                                                    : "text-yellow-300 fill-yellow-300 drop-shadow-md"
                                                : "text-gray-300 group-hover:text-gray-400"
                                        } group-hover:scale-110`}
                                    />
                                    {isActive && (
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <div className="w-4 h-4 md:w-12 md:h-12 bg-yellow-400 rounded-full opacity-20 animate-ping" />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.comments}>
                        {t.commentsLabel}
                    </LabelWithTag>
                </label>
                <textarea
                    value={surveyData.comments}
                    onChange={(e) => handleSurveyChange("comments", e.target.value)}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t.commentsPlaceholder}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.orderNo}</label>
                    <input
                        type="text"
                        value={surveyData.orderNumber}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.customerName}</label>
                    <input
                        type="text"
                        value={surveyData.customerName}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.address}</label>
                    <input
                        type="text"
                        value={formattedAddress || "N/A"}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    />
                </div>
                {surveyData.date && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t.date}</label>
                        <input
                            type="text"
                            value={moment(surveyData.date).format("DD MMM YYYY")}
                            readOnly
                            className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                )}
                {surveyData.time && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t.time}</label>
                        <input
                            type="text"
                            value={surveyData.time}
                            readOnly
                            className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                )}
            </div>

            <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                        <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.signature}>
                            {t.signature}
                        </LabelWithTag>
                    </label>
                    <button
                        type="button"
                        onClick={clearSignature}
                        className="px-3 py-1 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                    >
                        {t.clearSignature}
                    </button>
                </div>
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                    <canvas
                        ref={(el) => {
                            signatureRef.current = el;
                            if (el && surveyData.signature) {
                                drawSignatureOnCanvas(el, surveyData.signature);
                            }
                        }}
                        width={typeof window !== "undefined" && window.innerWidth <= 768 ? 600 : 400}
                        height={typeof window !== "undefined" && window.innerWidth <= 768 ? 300 : 150}
                        className="border border-gray-400 bg-white rounded w-full cursor-crosshair"
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                            minHeight: typeof window !== "undefined" && window.innerWidth <= 768 ? "300px" : "150px",
                            touchAction: "none",
                        }}
                        onMouseDown={(e) => drawHandlers(e, false)}
                        onTouchStart={(e) => drawHandlers(e, true)}
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                        {t.signatureHelp}
                    </p>
                </div>
            </div>

            {/* Review Screenshots Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Facebook Review */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                <FaFacebook className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.facebookReview}>
                                    {t.facebookReview}
                                </LabelWithTag>
                            </h3>
                        </div>
                        {facebookReviewUrl && (
                            <div className="flex items-center gap-2">
                                {getReviewStatusBadge(orderData?.survey?.facebookReview?.status)}
                            </div>
                        )}
                    </div>
                    
                    {facebookReviewUrl ? (
                        <div className="relative">
                            <img 
                                src={facebookReviewUrl} 
                                alt="Facebook Review" 
                                className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                            />
                            {orderData?.survey?.facebookReview?.status !== "approved" && (
                                <button
                                    type="button"
                                    onClick={() => facebookInputRef.current?.click()}
                                    disabled={isUploadingFacebook}
                                    className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 text-gray-700 text-sm rounded-lg shadow hover:bg-white transition-colors flex items-center gap-1"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    {t.replaceImage}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div 
                            onClick={() => !isUploadingFacebook && facebookInputRef.current?.click()}
                            className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors ${isUploadingFacebook ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isUploadingFacebook ? (
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mb-2" />
                                    <span className="text-gray-500">{t.uploading}</span>
                                </div>
                            ) : (
                                <>
                                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">{t.uploadImage}</p>
                                    <p className="text-xs text-gray-400 mt-1">{t.reviewScreenshot}</p>
                                </>
                            )}
                        </div>
                    )}
                    <input
                        ref={facebookInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={orderData?.survey?.facebookReview?.status === "approved"}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleReviewImageUpload(file, "facebook");
                            e.target.value = "";
                        }}
                    />
                </div>

                {/* Google Review */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                                <FaGoogle className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.googleReview}>
                                    {t.googleReview}
                                </LabelWithTag>
                            </h3>
                        </div>
                        {googleReviewUrl && (
                            <div className="flex items-center gap-2">
                                {getReviewStatusBadge(orderData?.survey?.googleReview?.status)}
                            </div>
                        )}
                    </div>
                    
                    {googleReviewUrl ? (
                        <div className="relative">
                            <img 
                                src={googleReviewUrl} 
                                alt="Google Review" 
                                className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                            />
                            {orderData?.survey?.googleReview?.status !== "approved" && (
                                <button
                                    type="button"
                                    onClick={() => googleInputRef.current?.click()}
                                    disabled={isUploadingGoogle}
                                    className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 text-gray-700 text-sm rounded-lg shadow hover:bg-white transition-colors flex items-center gap-1"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    {t.replaceImage}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div 
                            onClick={() => !isUploadingGoogle && googleInputRef.current?.click()}
                            className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/50 transition-colors ${isUploadingGoogle ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isUploadingGoogle ? (
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mb-2" />
                                    <span className="text-gray-500">{t.uploading}</span>
                                </div>
                            ) : (
                                <>
                                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">{t.uploadImage}</p>
                                    <p className="text-xs text-gray-400 mt-1">{t.reviewScreenshot}</p>
                                </>
                            )}
                        </div>
                    )}
                    <input
                        ref={googleInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={orderData?.survey?.googleReview?.status === "approved"}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleReviewImageUpload(file, "google");
                            e.target.value = "";
                        }}
                    />
                </div>
            </div>

            <div className="mt-12 flex justify-center gap-4 flex-wrap">
                <button
                    onClick={saveSurvey}
                    disabled={isSavingSurvey}
                    className={`group px-8 py-4 text-white rounded-2xl shadow-xl hover:shadow-2xl flex items-center space-x-3 transform transition-all duration-200 ${
                        isSavingSurvey
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 hover:scale-105"
                    }`}
                >
                    {isSavingSurvey ? (
                        <>
                            <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                            <span className="font-semibold text-lg">{t.saving}</span>
                        </>
                    ) : (
                        <>
                            <FiCheckCircle className="h-6 w-6 group-hover:animate-pulse" />
                            <span className="font-semibold text-lg">
                                <LabelWithTag code={CUSTOMER_SURVEY_STEP_CODES.saveSurvey}>
                                    {saved ? t.saved : t.saveSurvey}
                                </LabelWithTag>
                            </span>
                        </>
                    )}
                </button>
                <button
                    onClick={generateSurveyPDFOnly}
                    disabled={isSavingSurvey}
                    className="group px-8 py-4 text-white rounded-2xl shadow-xl hover:shadow-2xl flex items-center space-x-3 transform transition-all duration-200 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-105"
                >
                    <FiFileText className="h-6 w-6 group-hover:animate-bounce" />
                    <span className="font-semibold text-lg">{t.generatePdf}</span>
                </button>
            </div>
        </div>
    );
}
